import api from './api';

/**
 * #399 Quote Desk.
 *
 * Insurer side: `/insurers/insurance/quote-desk/` (`InsurerQuoteDeskViewSet`),
 * scoped server-side to the caller's OWN insurer. Broker side: the
 * `pricing-requests` actions on a quote request plus
 * `/quotes/pricing-requests/{id}/`. The server enforces every gate (module
 * permission, approval authority, the adjustment limit); the screens only
 * mirror it so a button is not offered where the API would refuse.
 */

export const QUOTE_DESK_URL = '/insurers/insurance/quote-desk/';

export type PricingStatus =
  | 'open'
  | 'in_review'
  | 'info_requested'
  | 'approved'
  | 'declined'
  | 'withdrawn'
  | 'expired';

export interface DeskPerson {
  id: string;
  name: string;
}

export interface DeskListRow {
  id: string;
  status: PricingStatus;
  status_display: string;
  requester_type: 'broker' | 'posp';
  origin: string;
  broker: { id: string; name: string };
  request_number: string;
  insurance_type: { id: string; name: string };
  proposer_name: string;
  key_figure: string | null;
  reference_premium: string | null;
  target_premium: string | null;
  proposed_premium: string | null;
  submitted_at: string;
  due_at: string;
  is_overdue: boolean;
  time_left_seconds: number | null;
  paused_at: string | null;
  assigned_to: DeskPerson | null;
  escalated_at: string | null;
  decided_at: string | null;
}

export interface DeskMessage {
  id: string;
  direction: 'insurer_to_broker' | 'broker_to_insurer';
  kind: string;
  body: string;
  author: string;
  created_at: string;
}

export interface DeskNote {
  id: string;
  body: string;
  author: DeskPerson | null;
  created_at: string;
  can_delete: boolean;
}

export interface DeskDetail extends DeskListRow {
  broker_note: string;
  proposed_product: { id: string; name: string } | null;
  proposed_valid_until: string | null;
  adjustment_pct: string | null;
  tat_hours: number;
  first_response_at: string | null;
  decision_code: string;
  decision_reason: string;
  escalation_note: string;
  quote: { id: string; total_premium: string; valid_until: string; status: string } | null;
  request: Record<string, unknown> & {
    request_number: string;
    vehicle: { make: string; model: string; variant: string } | null;
    members: { relation: string; age: number | null; pre_existing_conditions: boolean }[];
  };
  request_updated_since_submit: boolean;
  messages: DeskMessage[];
  notes: DeskNote[];
  events: {
    event: string;
    actor: DeskPerson | null;
    actor_label: string;
    from_status: string;
    to_status: string;
    created_at: string;
  }[];
  authority: {
    can_edit: boolean;
    can_approve: boolean;
    is_admin: boolean;
    adjust_limit_pct: string;
    max_approval_amount: string | null;
    validity_days: number;
  };
  products: { id: string; name: string }[];
  decline_codes: { value: string; label: string }[];
}

export interface DeskSummary {
  open: number;
  in_review: number;
  info_requested: number;
  overdue: number;
  due_within_4h: number;
  unassigned: number;
  decided_today: number;
  avg_first_response_hours_30d: number | null;
  desk_enabled: boolean;
}

export interface DeskSettings {
  quote_desk_enabled: boolean;
  quote_tat_hours: number;
  quote_adjust_limit_pct: string;
  quote_validity_days: number;
}

export interface DeskFilters {
  status?: string;
  overdue?: boolean;
  assigned?: string;
  requester_type?: string;
  search?: string;
  ordering?: string;
  page?: number;
}

interface Paginated<T> {
  count: number;
  total_pages?: number;
  current_page?: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

export const quoteDeskService = {
  async list(filters: DeskFilters = {}): Promise<Paginated<DeskListRow>> {
    const params: Record<string, string | number> = {};
    if (filters.status) params.status = filters.status;
    if (filters.overdue) params.overdue = 'true';
    if (filters.assigned) params.assigned = filters.assigned;
    if (filters.requester_type) params.requester_type = filters.requester_type;
    if (filters.search) params.search = filters.search;
    if (filters.ordering) params.ordering = filters.ordering;
    if (filters.page) params.page = filters.page;
    return (await api.get(QUOTE_DESK_URL, { params })).data;
  },
  async summary(): Promise<DeskSummary> {
    return (await api.get(`${QUOTE_DESK_URL}summary/`)).data;
  },
  async get(id: string): Promise<DeskDetail> {
    return (await api.get(`${QUOTE_DESK_URL}${id}/`)).data;
  },
  async action(id: string, name: string, body: Record<string, unknown> = {}): Promise<DeskDetail> {
    return (await api.post(`${QUOTE_DESK_URL}${id}/${name}/`, body)).data;
  },
  async addNote(id: string, body: string): Promise<DeskNote[]> {
    return (await api.post(`${QUOTE_DESK_URL}${id}/notes/`, { body })).data;
  },
  async deleteNote(id: string, noteId: string): Promise<void> {
    await api.delete(`${QUOTE_DESK_URL}${id}/notes/${noteId}/`);
  },
  async settings(): Promise<DeskSettings> {
    return (await api.get(`${QUOTE_DESK_URL}settings/`)).data;
  },
  async saveSettings(input: Partial<DeskSettings>): Promise<DeskSettings> {
    return (await api.patch(`${QUOTE_DESK_URL}settings/`, input)).data;
  },
};

// ---------------------------------------------------------------------------
// Broker / POSP side
// ---------------------------------------------------------------------------

export interface BrokerPricingRequest {
  id: string;
  insurer: { id: string; name: string; logo_url: string };
  status: PricingStatus;
  status_display: string;
  requester_type: 'broker' | 'posp';
  broker_note: string;
  target_premium: string | null;
  submitted_at: string;
  due_at: string;
  is_overdue: boolean;
  time_left_seconds: number | null;
  paused_at: string | null;
  messages: DeskMessage[];
  decision_code: string;
  decision_label: string;
  decision_reason: string;
  decided_at: string | null;
  quote: { id: string; total_premium: string; valid_until: string; status: string } | null;
  insurer_unavailable: boolean;
  can_reply: boolean;
  can_withdraw: boolean;
  /** The request changed after the insurer priced it (approved option only). */
  stale?: boolean;
}

export interface PricingInsurerChoice {
  id: string;
  name: string;
  eligible: boolean;
  reason: string;
  tat_hours: number;
}

export interface FlagInput {
  insurer: string;
  note: string;
  product?: string | null;
  target_premium?: string | null;
}

export const quotePricingService = {
  async list(requestId: string): Promise<BrokerPricingRequest[]> {
    const body = (await api.get(`/quotes/requests/${requestId}/pricing-requests/`)).data;
    // Defensive: only rows shaped like a pricing request are drawn.
    return Array.isArray(body)
      ? body.filter((row) => row && typeof row === 'object' && Array.isArray(row.messages) && row.insurer)
      : [];
  },
  async insurers(requestId: string): Promise<PricingInsurerChoice[]> {
    return (await api.get(`/quotes/requests/${requestId}/pricing-insurers/`)).data;
  },
  async flag(requestId: string, input: FlagInput): Promise<BrokerPricingRequest> {
    return (await api.post(`/quotes/requests/${requestId}/pricing-requests/`, input)).data;
  },
  async reply(id: string, message: string): Promise<BrokerPricingRequest> {
    return (await api.post(`/quotes/pricing-requests/${id}/reply/`, { message })).data;
  },
  async withdraw(id: string, reason = ''): Promise<BrokerPricingRequest> {
    return (await api.post(`/quotes/pricing-requests/${id}/withdraw/`, { reason })).data;
  },
};

/** The TAT badge, shared by the desk list and detail. */
export type TatState =
  | { kind: 'paused'; label: string }
  | { kind: 'overdue'; label: string }
  | { kind: 'soon'; label: string }
  | { kind: 'normal'; label: string }
  | { kind: 'closed'; label: string };

function hm(totalSeconds: number): string {
  const minutes = Math.max(0, Math.floor(totalSeconds / 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function tatState(
  row: Pick<DeskListRow, 'status' | 'due_at' | 'is_overdue' | 'time_left_seconds'>,
  now: number = Date.now()
): TatState {
  if (row.status === 'info_requested') return { kind: 'paused', label: 'Paused - waiting on broker' };
  if (row.status !== 'open' && row.status !== 'in_review') return { kind: 'closed', label: '—' };
  const left = Math.round((new Date(row.due_at).getTime() - now) / 1000);
  if (row.is_overdue || left <= 0) return { kind: 'overdue', label: `Overdue ${hm(-left)}` };
  if (left < 4 * 3600) return { kind: 'soon', label: `${hm(left)} left` };
  return { kind: 'normal', label: `${hm(left)} left` };
}
